import { useCallback, useEffect, useRef, useState } from "react";
import { Search } from "react-feather";
import { TbLocationCheck } from "react-icons/tb";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getBulkDropdownData,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import {
  approveLocationApprovalRequest,
  searchPendingLocationApprovalRequests,
} from "../../api/locationApprovalApi";
import Grid from "../../components/Grid";
import Button from "../../components/input/Button";
import Input from "../../components/input/Input";
import SearchDropdown from "../../components/input/SearchDropdown";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import ApproveLocation from "../../components/location/ApproveLocation";
import {
  ResizableScrollingTextRenderer,
  StatusCellStyle
} from "../../components/renderer/StyleRenderer";
import { formatFromJsonDate } from "../../dateConfig";

const LocationApproval = () => {
  const userData = useSelector((state) => state.user.userDetails);
  const user = useSelector((state) => state.user);
  const [tab, setTab] = useState(1);
  const gridRef = useRef();

  const [statusOptions, setStatusOptions] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [resetSearchFields, setResetSearchFields] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isApproving, setIsApproving] = useState(false);

  const [locationName, setLocationName] = useState("");
  const [customer, setCustomer] = useState("");
  // const [shipper, setShipper] = useState("");
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [status, setStatus] = useState(1);

  const [actionMenuOptions, setActionMenuOptions] = useState([]);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [updateNeeded, setUpdateNeeded] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { status: statusOpt = [] } = await getBulkDropdownData([
        { key: "status" },
      ]);
      setStatusOptions(statusOpt);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    const aWeekAgo = new Date();
    aWeekAgo.setDate(aWeekAgo.getDate() - 7);
    const formattedWeekAgo = aWeekAgo.toISOString().split("T")[0];
    const formattedToday = new Date().toISOString().split("T")[0];
    setFromDate(formattedWeekAgo);
    setToDate(formattedToday);
  }, []);

  const handleReset = (e) => {
    e.preventDefault();

    const aWeekAgo = new Date();
    aWeekAgo.setDate(aWeekAgo.getDate() - 7);
    const formattedWeekAgo = aWeekAgo.toISOString().split("T")[0];
    const formattedToday = new Date().toISOString().split("T")[0];
    setFromDate(formattedWeekAgo);
    setToDate(formattedToday);
    setResetSearchFields(true);
    setSearchFieldReset(true);
    setSearchClicked(false);
    setSearchRes([]);
    setSelectedRows([]);
    setLocationName("");
    setCustomer("");
    setLongitude("");
    setLatitude("");
    setStatus(1);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
    setTab(1);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleAccordionClick = (index) => {
    setActiveAccordion(index === activeAccordion ? null : index);
  };

  const handleSelectionChanged = useCallback(() => {
    if (gridRef.current) {
      const selectedData = gridRef.current.getSelectedData();
      setSelectedRows(selectedData);

      if (selectedData.length <= 0) {
        setShowActionMenu(false);
        setActionMenuOptions([]);
        return;
      }

      const menuList = [];

      menuList.push({
        key: 1,
        text: "Approve",
        value: 1,
      });

      setActionMenuOptions(menuList);

      setShowActionMenu(true);
    }
  }, []);

  const handleSearch = async () => {
    setSearchSubmitLoading(true);
    setSearchClicked(true);

    const response = await searchPendingLocationApprovalRequests({
      lorCustomer: customer,
      lorName: locationName,
      lorLon: longitude,
      lorLat: latitude,
      fromDate: fromDate,
      toDate: toDate,
      lorStatus: status,
    });

    const formattedResponse = response.map((loc) => ({
      ...loc,
      createdDate: loc.createdDate ? formatFromJsonDate(loc.createdDate) : "",
    }));

    setShowActionMenu(formattedResponse.length > 0);
    setSearchRes(formattedResponse);
    setSearchSubmitLoading(false);
    setActiveAccordion(false);
  };

  useEffect(() => {
    if (updateNeeded) {
      handleSearch();
      setUpdateNeeded(false);
    }
  }, [updateNeeded]);

  const actionMenuHandler = async (data) => {
    switch (data?.value) {
      case 1: {
        const selectedLorIds = [
          ...new Set(
            (selectedRows ?? [])
              .map((r) => r.lorId)
              .filter((id) => id !== null && id !== undefined),
          ),
        ];

        try {
          setIsApproving(true);
          const response = await approveLocationApprovalRequest({
            lorIds: selectedLorIds,
          });

          if (response.status === 200) {
            toast.success(response.data, {
              autoClose: 5000,
              containerId: "Main",
            });
            setUpdateNeeded(true);
          }
          setIsApproving(false);
        } catch (e) {
          if (e.response.data) {
            toast.error(e.response.data, {
              autoClose: 5000,
              containerId: "Main",
            });
          }
        } finally {
          setIsApproving(false);
        }
        break;
      }
      default:
    }
  };
  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular menu top attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <TbLocationCheck size={15} style={{ marginRight: "5px" }} />
            </span>
            Location Approval Search
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            onClick={handleDrawerOpen}
            editMode={false}
          />
        </div>
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion === 1 ? "active" : ""}`}
            onClick={() => handleAccordionClick(1)}
          >
            <div>
              Search
              <Search size={14} style={{ marginLeft: "10px" }} />
              <i className="dropdown icon"></i>
            </div>
          </div>
          <div className={`content ${activeAccordion === 1 ? "active" : ""}`}>
            <form className="ui form">
              <h4 className="ui dividing header">Location Information</h4>
              <div className="four fields">
                <SearchDropdown
                  key={1}
                  editMode={false}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setCustomer("");
                  }}
                  onResultSelect={(data) => setCustomer(data)}
                  api={getOtherRefSearchDropdown}
                  apiDependency={
                    userData.type === 1
                      ? {
                          type: "stakeholder_type",
                          value: "Customer",
                        }
                      : {
                          type: "user_stakeholder",
                          value: "Requesting Customer",
                          param2: user.username,
                        }
                  }
                  label="Customer"
                />
                <Input
                  type="text"
                  label="Location Name"
                  name="locationName"
                  value={locationName}
                  onChange={(e) => {
                    setLocationName(e.target.value);
                  }}
                />
                <Input
                  label="Longitude"
                  name="longitude"
                  value={longitude}
                  onChange={(e) => {
                    setLongitude(e.target.value);
                  }}
                />
                <Input
                  label="Latitude"
                  name="latitude"
                  value={latitude}
                  onChange={(e) => {
                    setLatitude(e.target.value);
                  }}
                />
              </div>
              {/* <div className="two fields"> */}

              {/* <SearchDropdown
                  key={2}
                  editMode={false}
                  reset={resetSearchFields}
                  setReset={setResetSearchFields}
                  clearable={true}
                  onClear={() => {
                    setShipper("");
                  }}
                  onResultSelect={(data) => setShipper(data)}
                  api={getOtherRefSearchDropdown}
                  apiDependency={
                    userData.type === 1
                      ? {
                          type: "stakeholder_type",
                          value: "Shipper",
                          param2: customer ? customer : "",
                        }
                      : {
                          type: "user_stakeholder",
                          value: "Shipper",
                          param1: customer ? customer : "",
                          param2: user.username,
                        }
                  }
                  label="Shipper/Plant"
                /> */}
              {/* </div> */}

              <div className="three fields">
                <Input
                  type="date"
                  label="From Date"
                  name="fromDate"
                  max={toDate ?? null}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                  }}
                />
                <Input
                  label="To Date"
                  type="date"
                  name="toDate"
                  min={fromDate}
                  value={toDate}
                  disabled={!fromDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                  }}
                />
                {/* <Dropdown
                  label="Status"
                  options={statusOptions}
                  placeholder={isLoading ? "loading..." : "Select Status"}
                  name="status"
                  style={{ height: "5px" }}
                  value={status}
                  onChange={(e, data) => {
                    setStatus(data.value);
                  }}
                  disabled={isLoading}
                /> */}
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div className="field" style={{ width: "100%" }}>
                  <Button template="search" onClick={handleSearch} />
                  <Button
                    template="searchReset"
                    onClick={(e) => {
                      handleReset(e);
                    }}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                ref={gridRef}
                heading="Pending Location approvals"
                content={searchRes}
                isLoading={searchSubmitLoading}
                multiRowSelection={true}
                onSelectionChanged={handleSelectionChanged}
                showActionMenu={showActionMenu}
                actionMenuOptions={actionMenuOptions}
                handleActionMenuChange={actionMenuHandler}
                config={[
                  {
                    headerName: "Code",
                    field: "lorCode",
                    headerCheckboxSelection: true,
                    checkboxSelection: true,
                  },
                  {
                    headerName: "Location",
                    field: "lorName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Customer",
                    field: "customerName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Longitude",
                    field: "lorLon",
                    minWidth: 230,
                  },
                  {
                    headerName: "Latitude",
                    field: "lorLat",
                    minWidth: 230,
                  },
                  {
                    headerName: "Shipper/Plant",
                    field: "plantName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },

                  {
                    headerName: "Status",
                    field: "statusName",
                    cellRenderer: StatusCellStyle,
                  },
                ]}
              />
            )}
          </>
        )}
      </div>

      {/* Side Drawer */}
      <SideDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          <div className="drawer-head" style={{ paddingBottom: "15px" }}>
            <h2>Create Location Approval Request</h2>
          </div>
        }
        body={
          <div className="drawer-content" style={{ width: "60vw" }}>
            <ApproveLocation handleDrawerClose={handleDrawerClose} setUpdateNeeded={setUpdateNeeded} />
          </div>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />

      {isApproving && (
        <div className="blur-overlay">
          <LoadingSpinner message={"Approving..."} />
        </div>
      )}
    </>
  );
};

export default LocationApproval;

const styles = `
  .blur-overlay {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
    z-index: 9999; /* Ensure it's on top of everything */
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .blurred {
    filter: blur(5px); /* Blur effect on content */
    pointer-events: none; /* Prevent interaction with blurred content */
  }
`;

document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);
